import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api, { enhancedApi } from '../api';
import PageLayout from '../components/ui/PageLayout';
import { StatCard } from '../components/ui/StatCard';
import PageHeaderActionsBar from '../components/ui/PageHeaderActionsBar';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  LinearProgress,
  Link,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as TaskIcon,
  Receipt as ReceiptIcon,
  Draw as DrawIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  EventAvailable as EventAvailableIcon,
  Note as NoteIcon,
  AccessTime as ClockIcon,
  Folder as FolderIcon,
  Message as MessageIcon,
  CloudUpload as UploadIcon,
  ThumbDown as DeclineIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

function getGreetingPeriod() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function getGreetingName() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'there';
    const u = JSON.parse(raw);
    return u.first_name || u.username || u.email?.split('@')[0] || 'there';
  } catch {
    return 'there';
  }
}

function getCurrentUserId() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u.id ?? u.user_id ?? null;
  } catch {
    return null;
  }
}

function formatTodayLong() {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function extractClientName(item) {
  if (item.client_name) return item.client_name;
  const t = item.title || '';
  const s = item.summary || '';
  if (item.type === 'note' && t.startsWith('Note added for ')) {
    return t.replace(/^Note added for /, '').trim();
  }
  if ((item.type === 'task_completed' || item.type === 'task') && s.startsWith('Client: ')) {
    return s.replace(/^Client: /, '').trim();
  }
  if ((item.type === 'invoice' || item.type === 'invoice_sent') && s.includes(' — ')) {
    return s.split(' — ')[0].trim();
  }
  return '';
}

function getActivityIcon(type) {
  const iconProps = { sx: { fontSize: 22 } };
  switch (type) {
    case 'task_completed':
    case 'task_created':
    case 'task':
      return <TaskIcon sx={{ ...iconProps.sx, color: 'warning.main' }} />;
    case 'invoice_sent':
    case 'invoice':
      return <ReceiptIcon sx={{ ...iconProps.sx, color: 'success.main' }} />;
    case 'note':
      return <NoteIcon sx={{ ...iconProps.sx, color: 'info.main' }} />;
    case 'file':
    case 'document':
      return <FolderIcon sx={{ ...iconProps.sx, color: 'secondary.main' }} />;
    case 'client_added':
    case 'client':
      return <PeopleIcon sx={{ ...iconProps.sx, color: 'primary.main' }} />;
    case 'payment_received':
    case 'payment':
      return <CreditCardIcon sx={{ ...iconProps.sx, color: 'warning.main' }} />;
    case 'event':
      return <EventAvailableIcon sx={{ ...iconProps.sx, color: 'info.main' }} />;
    case 'message':
      return <MessageIcon sx={{ ...iconProps.sx, color: 'primary.main' }} />;
    default:
      return <ScheduleIcon sx={{ ...iconProps.sx, color: 'text.secondary' }} />;
  }
}

function getClientActivityIcon(type) {
  const iconProps = { sx: { fontSize: 22 } };
  switch (type) {
    case 'esign_signed':
      return <DrawIcon sx={{ ...iconProps.sx, color: 'success.main' }} />;
    case 'esign_declined':
      return <DeclineIcon sx={{ ...iconProps.sx, color: 'error.main' }} />;
    case 'esign_viewed':
      return <ViewIcon sx={{ ...iconProps.sx, color: 'info.main' }} />;
    case 'file_uploaded':
      return <UploadIcon sx={{ ...iconProps.sx, color: 'secondary.main' }} />;
    case 'payment_received':
      return <CreditCardIcon sx={{ ...iconProps.sx, color: 'success.main' }} />;
    case 'message_received':
      return <MessageIcon sx={{ ...iconProps.sx, color: 'primary.main' }} />;
    default:
      return <ScheduleIcon sx={{ ...iconProps.sx, color: 'text.secondary' }} />;
  }
}

function isWorkflowDueOverdue(dueDateStr) {
  if (!dueDateStr) return false;
  const iso = dueDateStr.includes('T') ? dueDateStr : `${dueDateStr}T23:59:59Z`;
  return new Date(iso) < new Date();
}

function formatStageLabel(stageKey) {
  if (!stageKey) return '—';
  return String(stageKey)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const RECENT_ACTIVITY_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

function activityTypeLabel(type) {
  switch (type) {
    case 'note':
      return 'Note';
    case 'task_completed':
    case 'task_created':
    case 'task':
      return 'Task';
    case 'invoice_sent':
    case 'invoice':
      return 'Invoice';
    case 'client_added':
    case 'client':
      return 'Client';
    case 'event':
      return 'Event';
    default:
      return 'Activity';
  }
}

function flattenWorkflowPipeline(resData) {
  const rawStages = resData?.stages ?? resData ?? [];
  const out = [];

  if (Array.isArray(rawStages)) {
    rawStages.forEach((stageObj) => {
      const key = stageObj.id ?? stageObj.key ?? stageObj.stage;
      const label = stageObj.label || stageObj.name || formatStageLabel(key);
      const workflows = Array.isArray(stageObj.workflows) ? stageObj.workflows : [];
      workflows.forEach((w) => {
        if (key === 'completed' || w.progress_percentage === 100) return;
        out.push({ ...w, _stageKey: key, _stageLabel: label });
      });
    });
  } else if (rawStages && typeof rawStages === 'object') {
    Object.keys(rawStages).forEach((key) => {
      if (key === 'completed') return;
      const workflows = Array.isArray(rawStages[key]) ? rawStages[key] : [];
      workflows.forEach((w) => {
        if (w.progress_percentage === 100) return;
        out.push({ ...w, _stageKey: key, _stageLabel: formatStageLabel(key) });
      });
    });
  }
  return out;
}

function getTodaysEvents(eventsList) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return (eventsList || [])
    .filter((ev) => {
      const dt = ev.start_datetime || ev.due_date;
      if (!dt) return false;
      const d = new Date(dt);
      return d >= start && d < end;
    })
    .sort((a, b) => new Date(a.start_datetime || a.due_date) - new Date(b.start_datetime || b.due_date))
    .slice(0, 12);
}

const PENDING_ESIGN_STATUSES = ['sent_for_signature', 'in_progress', 'draft'];

function esignWaitingLabel(status) {
  if (status === 'sent_for_signature') return 'Signature pending';
  if (status === 'in_progress') return 'E-sign in progress';
  return 'E-sign pending';
}

function daysWaiting(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (86400000)));
}

const CompanyDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [wipWorkflows, setWipWorkflows] = useState([]);
  const [wipLoading, setWipLoading] = useState(true);
  const [wipFilter, setWipFilter] = useState('all');
  const [awaitingItems, setAwaitingItems] = useState([]);
  const [awaitingLoading, setAwaitingLoading] = useState(true);
  const [overdueTasksCount, setOverdueTasksCount] = useState(0);
  const [remindingId, setRemindingId] = useState(null);
  const [clientActivity, setClientActivity] = useState([]);
  const [clientActivityLoading, setClientActivityLoading] = useState(true);
  const [pendingTasksList, setPendingTasksList] = useState([]);
  const [pendingTasksLoading, setPendingTasksLoading] = useState(true);
  const [pendingClientRequestsCount, setPendingClientRequestsCount] = useState(0);
  const [pendingClientRequestsLoading, setPendingClientRequestsLoading] = useState(true);
  const navigate = useNavigate();
  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await enhancedApi.getDashboardV2();
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    let cancelled = false;
    const loadToday = async () => {
      try {
        setEventsLoading(true);
        const res = await api.get('/api/multi-tenant/events/');
        if (cancelled) return;
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : raw?.results ?? raw?.data ?? [];
        setTodaysEvents(getTodaysEvents(list));
      } catch {
        if (!cancelled) setTodaysEvents([]);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    };
    loadToday();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadWip = async () => {
      try {
        setWipLoading(true);
        const res = await api.get('/api/multi-tenant/workflow-pipeline/');
        if (cancelled) return;
        setWipWorkflows(flattenWorkflowPipeline(res.data));
      } catch {
        if (!cancelled) setWipWorkflows([]);
      } finally {
        if (!cancelled) setWipLoading(false);
      }
    };
    loadWip();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadAwaiting = async () => {
      try {
        setAwaitingLoading(true);
        const res = await enhancedApi.listESignatureDocuments();
        if (cancelled) return;
        const rows = res.data?.results ?? res.data ?? [];
        const list = Array.isArray(rows) ? rows : [];
        const pending = list.filter((doc) => PENDING_ESIGN_STATUSES.includes(doc.status));
        setAwaitingItems(pending.slice(0, 25));
      } catch {
        if (!cancelled) setAwaitingItems([]);
      } finally {
        if (!cancelled) setAwaitingLoading(false);
      }
    };
    loadAwaiting();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadOverdueTasks = async () => {
      try {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const taskRes = await enhancedApi.getTasks({}).catch(() => ({ data: {} }));
        if (cancelled) return;
        const taskRaw = taskRes.data?.results ?? taskRes.data?.tasks ?? taskRes.data;
        const tasks = Array.isArray(taskRaw) ? taskRaw : [];
        const isDone = (t) =>
          ['completed', 'done', 'cancelled', 'canceled'].includes(String(t.status || '').toLowerCase());
        const od = tasks.filter((t) => {
          if (!t.due_date || isDone(t)) return false;
          return new Date(t.due_date) < startOfDay;
        }).length;
        setOverdueTasksCount(od);
      } catch {
        if (!cancelled) setOverdueTasksCount(0);
      }
    };
    loadOverdueTasks();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadClientActivity = async () => {
      try {
        setClientActivityLoading(true);
        const res = await enhancedApi.getClientActivityFeed(15);
        if (cancelled) return;
        setClientActivity(res.data?.results ?? []);
      } catch {
        if (!cancelled) setClientActivity([]);
      } finally {
        if (!cancelled) setClientActivityLoading(false);
      }
    };
    loadClientActivity();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPendingClientRequests = async () => {
      try {
        setPendingClientRequestsLoading(true);
        const res = await api.get('/api/client-requests/', {
          params: { status: 'in_progress,overdue', page_size: 200 },
        });
        if (cancelled) return;
        const cnt = res.data?.count;
        const results = res.data?.results;
        setPendingClientRequestsCount(typeof cnt === 'number' ? cnt : Array.isArray(results) ? results.length : 0);
      } catch {
        if (!cancelled) setPendingClientRequestsCount(0);
      } finally {
        if (!cancelled) setPendingClientRequestsLoading(false);
      }
    };
    loadPendingClientRequests();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPendingTasks = async () => {
      try {
        setPendingTasksLoading(true);
        const res = await enhancedApi.getTasks({ status: 'pending' }).catch(() => ({ data: {} }));
        if (cancelled) return;
        const raw = res.data?.results ?? res.data?.tasks ?? res.data;
        const tasks = Array.isArray(raw) ? raw : [];
        const isDone = (t) =>
          ['completed', 'done', 'cancelled', 'canceled'].includes(String(t.status || '').toLowerCase());
        const pending = tasks.filter((t) => !isDone(t)).slice(0, 10);
        setPendingTasksList(pending);
      } catch {
        if (!cancelled) setPendingTasksList([]);
      } finally {
        if (!cancelled) setPendingTasksLoading(false);
      }
    };
    loadPendingTasks();
    return () => { cancelled = true; };
  }, []);

  const handleOptionSelect = (option) => {
    switch (option) {
      case 'notifications':
        navigate('/notifications');
        break;
      case 'inbox':
        navigate('/inbox');
        break;
      default:
        break;
    }
  };

  const handleSendReminder = async (docId) => {
    if (!docId) return;
    setRemindingId(docId);
    try {
      await enhancedApi.remindESignatureDocument(docId);
    } catch {
      /* silent */
    } finally {
      setRemindingId(null);
    }
  };

  const recentActivity = useMemo(() => {
    const rawActivitySource = data?.recent_activity ?? [];
    const now = Date.now();
    const cutoff = now - RECENT_ACTIVITY_MAX_AGE_MS;
    const mapped = [...rawActivitySource].map((item) => ({
      ...item,
      description: item.title || item.description || item.message,
      created_at: item.timestamp || item.created_at,
    }));
    const withTime = mapped.filter((item) => {
      const t = new Date(item.created_at || 0).getTime();
      if (!item.created_at || Number.isNaN(t) || t === 0) return false;
      return t >= cutoff;
    });
    return withTime
      .sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta;
      })
      .slice(0, 12);
  }, [data?.recent_activity]);

  const filteredWip = useMemo(() => {
    let rows = [...wipWorkflows];
    if (wipFilter === 'overdue') {
      rows = rows.filter((w) => isWorkflowDueOverdue(w.due_date));
    } else if (wipFilter === 'mine' && currentUserId != null) {
      rows = rows.filter(
        (w) =>
          w.assigned_to === currentUserId ||
          String(w.assigned_to) === String(currentUserId)
      );
    }
    return rows;
  }, [wipWorkflows, wipFilter, currentUserId]);

  if (loading) {
    return (
      <PageLayout onOptionSelect={handleOptionSelect} maxWidth="none" sx={{ width: '100%' }}>
        <Box sx={{ width: '100%' }}>
          <Skeleton variant="text" width={360} height={40} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={280} height={22} sx={{ mb: 3 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <StatCard key={i} label="" value={0} loading />
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
          </Box>
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout onOptionSelect={handleOptionSelect}>
        <Box sx={{ p: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" color="error" sx={{ fontWeight: 600, mb: 1 }}>
              Error loading dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="contained" size="small" onClick={fetchDashboard} sx={{ textTransform: 'none' }}>
              Try again
            </Button>
          </Paper>
        </Box>
      </PageLayout>
    );
  }

  const summary = data?.summary ?? {};
  const trends = data?.trends ?? {};
  const quickStats = data?.quick_stats ?? {};

  const activeClients = summary.active_clients ?? summary.active_clients_count ?? 0;
  const newClientsThisMonth = trends.new_clients_this_month;
  const clientsTrendPositive = newClientsThisMonth != null && Number(newClientsThisMonth) > 0;

  const overdueWorkflows = summary.overdue_workflows ?? summary.overdue_workflows_count ?? 0;

  const awaitingDocCount = summary.pending_document_requests ?? summary.pending_doc_requests_count ?? 0;
  const esigsPending = quickStats.esignatures_pending ?? 0;

  const greetingWord = getGreetingPeriod();
  const greeting = getGreetingName();
  const dateLine = formatTodayLong();

  return (
    <PageLayout
      onOptionSelect={handleOptionSelect}
      maxWidth="none"
      sx={{ width: '100%' }}
    >
      <Box sx={{ width: '100%', minHeight: 0 }}>
        <PageHeaderActionsBar
          title={`${greetingWord}, ${greeting}!`}
          description={dateLine}
          secondaryActions={[
            { label: 'Add Client', icon: <PeopleIcon />, onClick: () => navigate('/client_list') },
            { label: 'Add Event', icon: <EventAvailableIcon />, onClick: () => navigate('/events') },
            { label: 'Add Invoice', icon: <ReceiptIcon />, onClick: () => navigate('/billing/invoices/create') },
          ]}
          primaryAction={{ label: 'Add Task', icon: <TaskIcon />, onClick: () => navigate('/tasks') }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box onClick={() => navigate('/client_list')} sx={{ cursor: 'pointer' }}>
            <StatCard
              label="Active Clients"
              value={activeClients}
              sublabel={clientsTrendPositive ? `+${newClientsThisMonth} this month` : undefined}
              sublabelTone={clientsTrendPositive ? 'success' : 'neutral'}
            />
          </Box>
          <Box onClick={() => navigate('/tasks')} sx={{ cursor: 'pointer' }}>
            <StatCard
              label="Pending Tasks"
              value={summary.pending_tasks ?? summary.pending_tasks_count ?? 0}
              sublabel={overdueTasksCount > 0 ? `${overdueTasksCount} overdue` : undefined}
              sublabelTone={overdueTasksCount > 0 ? 'danger' : 'neutral'}
            />
          </Box>
          <Box onClick={() => navigate('/workflows')} sx={{ cursor: 'pointer' }}>
            <StatCard
              label="Overdue Workflows"
              value={overdueWorkflows}
              sublabel={Number(overdueWorkflows) > 0 ? 'action required' : 'all on track'}
              sublabelTone={Number(overdueWorkflows) > 0 ? 'danger' : 'success'}
            />
          </Box>
          <Box onClick={() => navigate('/inbox')} sx={{ cursor: 'pointer' }}>
            <StatCard
              label="Pending Doc Requests"
              value={awaitingDocCount}
              sublabel="awaiting clients"
              sublabelTone="neutral"
            />
          </Box>
          <Box onClick={() => navigate('/workflows')} sx={{ cursor: 'pointer' }}>
            <StatCard
              label="E-Sigs Pending"
              value={esigsPending}
              sublabel="sent for signature"
              sublabelTone="neutral"
            />
          </Box>
          <Box
            onClick={() => navigate('/client-requests?status=in_progress,overdue')}
            sx={{ cursor: 'pointer' }}
          >
            <StatCard
              label="Pending Requests"
              value={pendingClientRequestsCount}
              sublabel="in progress or overdue"
              sublabelTone={pendingClientRequestsCount > 0 ? 'warning' : 'neutral'}
              loading={pendingClientRequestsLoading}
            />
          </Box>
        </Box>

        {/* ── Row 1: Pending Tasks + Today's Schedule ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Pending Tasks
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/tasks')}
                  sx={{ fontWeight: 600, cursor: 'pointer' }}
                >
                  View all
                </Link>
              </Box>
              {pendingTasksLoading ? (
                <Box>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : pendingTasksList.length === 0 ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                  <TaskIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No pending tasks
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/tasks')}
                    sx={{ textTransform: 'none' }}
                  >
                    Add task
                  </Button>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                  <List dense disablePadding>
                    {pendingTasksList.map((task, idx) => (
                      <ListItem
                        key={task.id ?? idx}
                        disablePadding
                        sx={{
                          py: 1.25,
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => navigate('/tasks')}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <TaskIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {task.title || task.name || 'Task'}
                            </Typography>
                          }
                          secondary={
                            task.due_date
                              ? new Date(
                                  task.due_date.includes('T') ? task.due_date : `${task.due_date}T12:00:00`
                                ).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                              : task.client_name || null
                          }
                          secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        />
                        {overdueTasksCount > 0 && task.due_date && new Date(task.due_date) < new Date() && (
                          <Chip
                            label="Overdue"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                minHeight: 320,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Today&apos;s Schedule
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/events')}
                  sx={{ fontWeight: 600, cursor: 'pointer' }}
                >
                  View all
                </Link>
              </Box>
              {eventsLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : todaysEvents.length > 0 ? (
                <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                  <List dense disablePadding>
                    {todaysEvents.map((ev) => (
                      <ListItem
                        key={ev.id}
                        disablePadding
                        sx={{
                          py: 1.25,
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => navigate('/events')}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ScheduleIcon fontSize="small" sx={{ color: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {ev.start_datetime
                                ? new Date(ev.start_datetime).toLocaleTimeString(undefined, {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })
                                : '—'}{' '}
                              · {ev.title || 'Event'}
                            </Typography>
                          }
                          secondary={ev.client_name || ev.client?.display_name || ev.client?.name || null}
                          secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                  <ClockIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No events scheduled today
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/events')}
                    sx={{ textTransform: 'none' }}
                  >
                    Add event
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* ── Remaining dashboard widgets ── */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8} sx={{ minHeight: 0, minWidth: 0 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                mb: 3,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Work in Progress
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <ToggleButtonGroup
                    size="small"
                    value={wipFilter}
                    exclusive
                    onChange={(_, v) => v && setWipFilter(v)}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="mine">My Work</ToggleButton>
                    <ToggleButton value="overdue">Overdue</ToggleButton>
                  </ToggleButtonGroup>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/workflows')}
                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                  >
                    View All
                  </Link>
                </Box>
              </Box>
              {wipLoading ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <TableContainer
                  component="div"
                  sx={{
                    maxHeight: { xs: 280, sm: 360, md: 440 },
                    overflow: 'auto',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    overscrollBehavior: 'contain',
                  }}
                >
                  <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: 'background.paper' }}>Client</TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: 'background.paper' }}>Service</TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: 'background.paper' }}>Stage</TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: 'background.paper' }}>Due</TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: 'background.paper' }}>Assigned</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 120, backgroundColor: 'background.paper' }}>Progress</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredWip.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No active workflows match this filter.
                              </Typography>
                              {wipFilter !== 'all' && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setWipFilter('all')}
                                  sx={{ textTransform: 'none', color: 'primary.main' }}
                                >
                                  Show all workflows
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredWip.map((w) => (
                          <TableRow
                            key={w.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => w.client_id && navigate(`/client/${w.client_id}`)}
                          >
                            <TableCell>{w.client_name || '—'}</TableCell>
                            <TableCell>{w.template_name || w.service || '—'}</TableCell>
                            <TableCell>{w._stageLabel || formatStageLabel(w.pipeline_stage)}</TableCell>
                            <TableCell>
                              {w.due_date
                                ? new Date(
                                    w.due_date.includes('T') ? w.due_date : `${w.due_date}T12:00:00`
                                  ).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                                : '—'}
                            </TableCell>
                            <TableCell>{w.assigned_to_name || '—'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, w.progress_percentage ?? 0)}
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                    '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36 }}>
                                  {w.progress_percentage ?? 0}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Recent Activity
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    Last 3 days · newest first · unread items highlighted
                  </Typography>
                </Box>
                <Link
                  component={RouterLink}
                  to="/activity-log"
                  variant="body2"
                  sx={{ fontWeight: 600, flexShrink: 0 }}
                >
                  View all
                </Link>
              </Box>
              {recentActivity.length > 0 ? (
                <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                  <List dense disablePadding>
                    {recentActivity.map((item, idx) => {
                      const headline =
                        item.title || item.description || item.message || item.summary || 'Activity';
                      const detailRaw = item.summary;
                      const detail =
                        detailRaw && String(detailRaw).trim() && detailRaw !== headline
                          ? String(detailRaw).trim()
                          : '';
                      const clientNm = extractClientName(item);
                      const ts = item.created_at
                        ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
                        : '';
                      const actor = item.user && String(item.user).trim() ? item.user : null;
                      const isRead = item.is_read === true;
                      return (
                        <ListItem
                          key={item.id ?? idx}
                          disablePadding
                          sx={{
                            py: 1.5,
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                            alignItems: 'flex-start',
                            opacity: isRead ? 0.55 : 1,
                            bgcolor: isRead ? 'transparent' : 'action.hover',
                            borderLeftWidth: isRead ? 0 : 3,
                            borderLeftStyle: 'solid',
                            borderLeftColor: isRead ? 'transparent' : 'primary.main',
                            pl: isRead ? 0 : 1,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{getActivityIcon(item.type)}</ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  gap: 1.5,
                                  mb: detail || clientNm || actor ? 0.5 : 0,
                                }}
                              >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                                    <Chip
                                      label={activityTypeLabel(item.type)}
                                      size="small"
                                      variant={isRead ? 'outlined' : 'filled'}
                                      color={isRead ? 'default' : 'primary'}
                                      sx={{ height: 22, fontWeight: 600, fontSize: '0.75rem' }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: isRead ? 500 : 700,
                                      lineHeight: 1.35,
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {headline}
                                  </Typography>
                                </Box>
                                {ts ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pt: 0.25 }}>
                                    {ts}
                                  </Typography>
                                ) : null}
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'block' }}>
                                {detail ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      mb: clientNm || actor ? 0.75 : 0,
                                    }}
                                  >
                                    {detail}
                                  </Typography>
                                ) : null}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                                  {clientNm ? (
                                    <Link
                                      component={RouterLink}
                                      to="/client_list"
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {clientNm}
                                    </Link>
                                  ) : null}
                                  {actor ? (
                                    <Typography variant="caption" color="text.secondary" component="span">
                                      {clientNm ? '· ' : ''}
                                      {actor}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ) : (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <ClockIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                  <Typography variant="body2" color="text.secondary">
                    No recent activity
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                Client Activity
              </Typography>
              {clientActivityLoading ? (
                <Box>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : clientActivity.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <ScheduleIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                  <Typography variant="body2" color="text.secondary">
                    No client activity yet
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {clientActivity.map((item) => {
                    const ts = item.timestamp
                      ? formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })
                      : '';
                    return (
                      <ListItem
                        key={item.id ?? item.timestamp ?? item.title}
                        disablePadding
                        sx={{
                          py: 1.25,
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                          alignItems: 'flex-start',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          {getClientActivityIcon(item.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {ts}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            item.client_name ? (
                              <Link
                                component={RouterLink}
                                to={item.client_id ? `/client_list/${item.client_id}` : '/client_list'}
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                              >
                                {item.client_name}
                              </Link>
                            ) : null
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                Awaiting E-Signatures
              </Typography>
              {awaitingLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : awaitingItems.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1, display: 'block', mx: 'auto' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    All clients up to date!
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 420, overflowY: 'auto', overscrollBehavior: 'contain', pr: 0.5, mr: -0.5 }}>
                  <List dense disablePadding>
                    {awaitingItems.map((doc) => {
                      const name =
                        doc.client_name || doc.signer_name || doc.document_name || `Document ${doc.id}`;
                      const waiting = esignWaitingLabel(doc.status);
                      const sent = doc.sent_at || doc.updated_at || doc.created_at;
                      const days = daysWaiting(sent);
                      return (
                        <ListItem
                          key={doc.id}
                          disablePadding
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            py: 1.5,
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {name}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={remindingId === doc.id}
                              onClick={() => handleSendReminder(doc.id)}
                              sx={{ textTransform: 'none', flexShrink: 0 }}
                            >
                              {remindingId === doc.id ? 'Sending…' : 'Send Reminder'}
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {waiting}
                            {days > 0 ? ` · ${days} day${days === 1 ? '' : 's'} waiting` : ''}
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </PageLayout>
  );
};

export default CompanyDashboard;
